import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ChevronLeft, Copy, Pencil, Plus, Trash2 } from "lucide-react";

const CV_KEYS = [
  { key: "show_prices", label: "Show Prices", desc: "Per-unit rate visible on orders, PFI, reports" },
  { key: "show_totals", label: "Show Totals", desc: "Grand total, taxable value, line totals" },
  { key: "show_gst", label: "Show GST", desc: "CGST/SGST/IGST columns on reports & PFI" },
  { key: "show_payment_status", label: "Show Payment Status", desc: "Pending / Advance Received / Credit Approved / Hold / Rejected" },
];

const EMPTY_ROLE = {
  slug: "",
  name: "",
  description: "",
  modules: [],
  actions: [],
  commercial_visibility: { show_prices: true, show_totals: true, show_gst: true, show_payment_status: true },
};

export default function SettingsRoles() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [taxonomy, setTaxonomy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: "create", data: EMPTY_ROLE });
  const [cloneDialog, setCloneDialog] = useState({ open: false, source: null, slug: "", name: "" });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, role: null });
  const [saving, setSaving] = useState(false);

  const isSA = user?.role === "super_admin";

  async function loadAll() {
    setLoading(true);
    try {
      const [rolesRes, taxRes] = await Promise.all([
        api.get("/roles"),
        api.get("/permissions/taxonomy"),
      ]);
      setRoles(rolesRes.data);
      setTaxonomy(taxRes.data);
    } catch (e) {
      toast.error("Failed to load roles: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (isSA) loadAll(); /* eslint-disable-line */ }, [isSA]);

  if (user && !isSA) return <Navigate to="/" replace />;

  function openCreate() {
    setDialog({ open: true, mode: "create", data: { ...EMPTY_ROLE, commercial_visibility: { ...EMPTY_ROLE.commercial_visibility } } });
  }
  function openEdit(role) {
    setDialog({ open: true, mode: "edit", data: {
      slug: role.slug,
      name: role.name || "",
      description: role.description || "",
      modules: [...(role.modules || [])],
      actions: [...(role.actions || [])],
      commercial_visibility: { ...(role.commercial_visibility || EMPTY_ROLE.commercial_visibility) },
      is_system: !!role.is_system,
    } });
  }
  function openClone(source) {
    setCloneDialog({ open: true, source, slug: "", name: `Copy of ${source.name}` });
  }

  async function saveRole() {
    const d = dialog.data;
    if (dialog.mode === "create") {
      if (!d.slug || !d.name) { toast.error("Slug and name are required"); return; }
    }
    setSaving(true);
    try {
      if (dialog.mode === "create") {
        await api.post("/roles", d);
        toast.success("Role created");
      } else {
        // super_admin edit: only name/description allowed by backend
        const payload = d.slug === "super_admin"
          ? { name: d.name, description: d.description }
          : { name: d.name, description: d.description, modules: d.modules, actions: d.actions, commercial_visibility: d.commercial_visibility };
        await api.patch(`/roles/${d.slug}`, payload);
        toast.success("Role updated");
      }
      setDialog({ open: false, mode: "create", data: EMPTY_ROLE });
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function doClone() {
    if (!cloneDialog.slug || !cloneDialog.name) { toast.error("Slug and name are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/roles/${cloneDialog.source.slug}/clone`, { slug: cloneDialog.slug, name: cloneDialog.name });
      toast.success(`Cloned from ${cloneDialog.source.slug}`);
      setCloneDialog({ open: false, source: null, slug: "", name: "" });
      await loadAll();
      // Open editor for the new role
      setTimeout(() => openEdit(data), 120);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    } finally { setSaving(false); }
  }

  async function doDelete() {
    setSaving(true);
    try {
      await api.delete(`/roles/${deleteDialog.role.slug}`);
      toast.success("Role deleted");
      setDeleteDialog({ open: false, role: null });
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="space-y-6" data-testid="settings-roles-root">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
            <Link to="/settings" className="hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-to-settings">
              <ChevronLeft className="w-4 h-4" /> Settings
            </Link>
            <span>/</span>
            <span className="text-slate-700">Roles</span>
          </div>
          <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">Roles</h1>
          <p className="text-sm text-slate-500 mt-1">Manage role templates — modules, actions, and commercial visibility. User overrides always win.</p>
        </div>
        <Button onClick={openCreate} data-testid="create-role-btn" className="gap-2">
          <Plus className="w-4 h-4" /> New Role
        </Button>
      </div>

      <Card className="p-0 border border-slate-200 shadow-none rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Slug</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium text-right">Modules</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
              <th className="px-4 py-3 font-medium text-right">Users</th>
              <th className="px-4 py-3 font-medium w-32"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && roles.map((r) => (
              <tr key={r.slug} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`role-row-${r.slug}`}>
                <td className="px-4 py-3 font-mono text-slate-700">{r.slug}</td>
                <td className="px-4 py-3 text-slate-900">
                  {r.name}
                  {r.description && <div className="text-xs text-slate-500 mt-0.5 truncate max-w-md">{r.description}</div>}
                </td>
                <td className="px-4 py-3">
                  {r.is_system
                    ? <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">System</Badge>
                    : <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200">Custom</Badge>}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{(r.modules || []).length}</td>
                <td className="px-4 py-3 text-right tabular-nums">{(r.actions || []).length}</td>
                <td className="px-4 py-3 text-right tabular-nums">{r.usage_count ?? 0}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-role-${r.slug}`} title="Edit">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => openClone(r)} data-testid={`clone-role-${r.slug}`} title="Clone">
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon" variant="ghost"
                      disabled={r.is_system || r.usage_count > 0}
                      onClick={() => setDeleteDialog({ open: true, role: r })}
                      data-testid={`delete-role-${r.slug}`}
                      title={r.is_system ? "System roles cannot be deleted" : r.usage_count > 0 ? `${r.usage_count} user(s) use this role` : "Delete"}
                    >
                      <Trash2 className={`w-4 h-4 ${r.is_system || r.usage_count > 0 ? "opacity-30" : "text-red-600"}`} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <RoleFormDialog
        state={dialog}
        setState={setDialog}
        taxonomy={taxonomy}
        onSave={saveRole}
        saving={saving}
      />

      <Dialog open={cloneDialog.open} onOpenChange={(v) => !v && setCloneDialog({ open: false, source: null, slug: "", name: "" })}>
        <DialogContent data-testid="clone-role-dialog">
          <DialogHeader>
            <DialogTitle>Clone Role: {cloneDialog.source?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>New Slug</Label>
              <Input
                value={cloneDialog.slug}
                onChange={(e) => setCloneDialog({ ...cloneDialog, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                placeholder="sales_karnataka"
                data-testid="clone-slug-input"
              />
              <p className="text-xs text-slate-500 mt-1">Lowercase letters, numbers, underscores. Starts with a letter.</p>
            </div>
            <div>
              <Label>Display Name</Label>
              <Input
                value={cloneDialog.name}
                onChange={(e) => setCloneDialog({ ...cloneDialog, name: e.target.value })}
                data-testid="clone-name-input"
              />
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 rounded p-3">
              Copies all modules, actions, and commercial visibility from <span className="font-mono">{cloneDialog.source?.slug}</span>. You can edit them right after.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneDialog({ open: false, source: null, slug: "", name: "" })}>Cancel</Button>
            <Button onClick={doClone} disabled={saving} data-testid="clone-confirm-btn">Clone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog.open} onOpenChange={(v) => !v && setDeleteDialog({ open: false, role: null })}>
        <AlertDialogContent data-testid="delete-role-alert">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role "{deleteDialog.role?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The role will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function RoleFormDialog({ state, setState, taxonomy, onSave, saving }) {
  const { open, mode, data } = state;
  const modules = taxonomy?.modules || [];
  const actions = taxonomy?.actions || [];
  const isSuperAdmin = data.slug === "super_admin";
  const permsLocked = mode === "edit" && isSuperAdmin;

  function setField(k, v) { setState({ ...state, data: { ...data, [k]: v } }); }
  function toggleIn(list, key) { return list.includes(key) ? list.filter((x) => x !== key) : [...list, key]; }
  function toggleModule(m) { setField("modules", toggleIn(data.modules || [], m)); }
  function toggleAction(a) { setField("actions", toggleIn(data.actions || [], a)); }
  function toggleCV(k) { setField("commercial_visibility", { ...(data.commercial_visibility || {}), [k]: !(data.commercial_visibility || {})[k] }); }
  function selectAllModules() { setField("modules", [...modules]); }
  function clearModules() { setField("modules", []); }
  function selectAllActions() { setField("actions", [...actions]); }
  function clearActions() { setField("actions", []); }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && setState({ open: false, mode: "create", data: EMPTY_ROLE })}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="role-form-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "New Role" : `Edit Role: ${data.slug}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {permsLocked && (
            <div className="text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded p-3">
              <span className="font-medium">super_admin</span> permissions are hardcoded for security and cannot be changed. You can only edit the display name / description.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Slug</Label>
              <Input
                value={data.slug}
                onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                disabled={mode === "edit"}
                placeholder="sales_karnataka"
                data-testid="role-slug-input"
              />
              <p className="text-xs text-slate-500 mt-1">Lowercase / numbers / underscore. {mode === "edit" ? "Immutable after creation." : ""}</p>
            </div>
            <div>
              <Label>Display Name</Label>
              <Input value={data.name} onChange={(e) => setField("name", e.target.value)} placeholder="Karnataka Sales" data-testid="role-name-input" />
            </div>
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea rows={2} value={data.description || ""} onChange={(e) => setField("description", e.target.value)} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Modules ({(data.modules || []).length}/{modules.length})</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="ghost" onClick={selectAllModules} disabled={permsLocked} data-testid="modules-all">All</Button>
                <Button type="button" size="sm" variant="ghost" onClick={clearModules} disabled={permsLocked} data-testid="modules-none">None</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 bg-slate-50 rounded p-3">
              {modules.map((m) => (
                <label key={m} className={`flex items-center gap-2 text-sm ${permsLocked ? "text-slate-400" : "text-slate-700 cursor-pointer"}`}>
                  <input
                    type="checkbox"
                    checked={(data.modules || []).includes(m)}
                    onChange={() => toggleModule(m)}
                    disabled={permsLocked}
                    data-testid={`role-mod-${m}`}
                  />
                  <span className="font-mono text-xs">{m}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Actions ({(data.actions || []).length}/{actions.length})</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="ghost" onClick={selectAllActions} disabled={permsLocked} data-testid="actions-all">All</Button>
                <Button type="button" size="sm" variant="ghost" onClick={clearActions} disabled={permsLocked} data-testid="actions-none">None</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 bg-slate-50 rounded p-3">
              {actions.map((a) => (
                <label key={a} className={`flex items-center gap-2 text-sm ${permsLocked ? "text-slate-400" : "text-slate-700 cursor-pointer"}`}>
                  <input
                    type="checkbox"
                    checked={(data.actions || []).includes(a)}
                    onChange={() => toggleAction(a)}
                    disabled={permsLocked}
                    data-testid={`role-act-${a}`}
                  />
                  <span className="font-mono text-xs">{a}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold">Commercial Visibility</Label>
            <div className="space-y-2 mt-2">
              {CV_KEYS.map((cv) => (
                <div key={cv.key} className="flex items-start justify-between gap-4 bg-slate-50 rounded p-3">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{cv.label}</div>
                    <div className="text-xs text-slate-500">{cv.desc}</div>
                  </div>
                  <Switch
                    checked={!!(data.commercial_visibility || {})[cv.key]}
                    onCheckedChange={() => toggleCV(cv.key)}
                    disabled={permsLocked}
                    data-testid={`role-cv-${cv.key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setState({ open: false, mode: "create", data: EMPTY_ROLE })}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="save-role-btn">{mode === "create" ? "Create Role" : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
